import { useEffect, useState } from 'react';
import { zGarment, zLook, zPage, type Garment, type Look, type MoodId } from '@racksavant/core';

/* =========================================================================
   Live catalogue reads for the public site.

   Same contract as `useStats`: the marketing site may state facts about the
   product, and may not invent them. So both hooks here have exactly one
   failure mode — an empty array — and every caller must read correctly with
   nothing in it. No skeleton catalogue, no seeded stand-in rows, no "and 200
   more". If the rack is empty or the network is down, the section says
   nothing rather than something untrue.

   These are the two public, unauthenticated collection endpoints:

     GET /v1/garments?mood=…   the rack, filtered by mood
     GET /v1/feed?scope=runway the public Runway

   Both are read through the same zod schemas the API validates them with, so
   a shape change upstream degrades to "no items" rather than to a blank page
   with a thrown render.
   ========================================================================= */

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8787').replace(/\/+$/, '');

const zGarmentPage = zPage(zGarment);
const zLookPage = zPage(zLook);

/**
 * Media on this API is absolute already, but a self-hosted or proxied deploy
 * can return a root-relative key. Resolving it here keeps every caller from
 * having to know that.
 */
export function mediaSrc(value: string | null | undefined): string {
  if (!value) return '';
  if (/^(https?:|data:|blob:)/.test(value)) return value;
  return `${API_URL}${value.startsWith('/') ? '' : '/'}${value}`;
}

async function readPage<T>(url: string, parse: (value: unknown) => T[], signal?: AbortSignal): Promise<T[]> {
  const response = await fetch(url, { signal });
  if (!response.ok) return [];
  return parse(await response.json());
}

/** Real pieces on the rack carrying this mood. Empty means "say nothing". */
export function useGarments(mood: MoodId, limit = 12): Garment[] {
  const [items, setItems] = useState<Garment[]>([]);

  useEffect(() => {
    let live = true;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();

    // Switching moods clears first: showing the previous mood's pieces under a
    // new heading would be a small lie, and this page is built out of true ones.
    setItems([]);

    readPage(
      `${API_URL}/v1/garments?mood=${encodeURIComponent(mood)}&sort=trending&limit=${limit}`,
      (value) => {
        const parsed = zGarmentPage.safeParse(value);
        return parsed.success ? parsed.data.items : [];
      },
      controller?.signal,
    )
      .then((next) => {
        if (live) setItems(next);
      })
      .catch(() => {
        /* An empty rack is a supported state. */
      });

    return () => {
      live = false;
      controller?.abort();
    };
  }, [mood, limit]);

  return items;
}

/** Real public looks from the Runway. Empty means "say nothing". */
export function useRunway(limit = 6): Look[] {
  const [items, setItems] = useState<Look[]>([]);

  useEffect(() => {
    let live = true;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();

    readPage(
      `${API_URL}/v1/feed?scope=runway&limit=${limit}`,
      (value) => {
        const parsed = zLookPage.safeParse(value);
        return parsed.success ? parsed.data.items : [];
      },
      controller?.signal,
    )
      .then((next) => {
        if (live) setItems(next);
      })
      .catch(() => {
        /* An empty Runway is a supported state. */
      });

    return () => {
      live = false;
      controller?.abort();
    };
  }, [limit]);

  return items;
}
